import { normalizeAppPath } from '~/navigation/isInAppPath'

/**
 * After an org switch, keep the current route when it is org-agnostic, but drop
 * entity-detail / settings-edit segments so we do not land on an asset that may
 * not belong to the target org.
 *
 * Static hub segments under a section (e.g. `/chargers/rollouts`, `/reports/energy`)
 * stay put; only dynamic entity ids under the section root collapse to the root.
 *
 * Returned paths always include a trailing slash to match `trailingSlash: true`
 * and `usePathname()`, so hub/Exit navigations that stay on the same page skip
 * a no-op `router.replace` (and an extra 308).
 */

/** Second-segment hubs that are not entity detail pages. */
const SECTION_HUBS: Readonly<Record<string, ReadonlySet<string>>> = {
  assistant: new Set(['conversations']),
  chargeai: new Set([
    'billing-cycles',
    'charging-optimization',
    'plan-inspector',
    'vehicle-readiness'
  ]),
  chargers: new Set(['rollouts']),
  operations: new Set([
    'documents',
    'issues',
    'plans',
    'projects',
    'settings',
    'templates',
    'tickets'
  ]),
  reports: new Set(['energy', 'export', 'v2', 'v3', 'vehicles'])
}

export default function resolveRouteAfterOrgSwitch(pathname: string): string {
  const parts = pathname.split('/').filter(Boolean)
  if (parts.length === 0) return '/'

  if (parts[0] === 'settings') {
    if (parts.length >= 3) {
      return normalizeAppPath(`/settings/${parts[1]}`)
    }
    return normalizeAppPath(pathname)
  }

  if (parts.length === 1) {
    return normalizeAppPath(pathname)
  }

  const section = parts[0]!
  const second = parts[1]!
  const hubs = SECTION_HUBS[section]
  if (hubs?.has(second)) {
    // Keep the hub list route; strip entity ids nested under it
    // (/chargers/rollouts/abc → /chargers/rollouts/).
    return normalizeAppPath(`/${section}/${second}`)
  }

  // /vehicles/abc-123 → /vehicles/ ; /vehicles → /vehicles/
  return normalizeAppPath(`/${section}`)
}
